/*
 * Copyright © 2017-2021 Skale Education Inc. - All Rights Reserved
 */

import React, { useState } from "react";
import {
  StyleProp,
  TextStyle,
  TouchableOpacity,
  View,
  ViewStyle,
} from "react-native";

import Rodal from "rodal";
import "rodal/lib/rodal.css";
import Icon from "react-icons-kit";
import { closeRound } from "react-icons-kit/ionicons/closeRound";

import { Text, WarningBanner } from "UpduoLibrary";
import { skaleBlack } from "config/UpduoColors";

interface SimpleModalProps {
  isVisible: boolean;
  onClose: Function;
  rodalStyle?: Object;
  showX?: boolean;
  containerStyle?: StyleProp<ViewStyle>;
  heading?: string;
  headingStyle?: TextStyle;
  description?: string;
  descriptionStyle?: TextStyle;
  buttons?: React.ReactNode[];
  warningMessage?: string;
}

/**
 * A simple modal component that displays:
 *   - A heading
 *   - A description
 *   - A list of buttons
 *   - An X button to close the modal
 * , all of which are optional.
 *
 * Selected props are documented below:
 *   showX : whether to show the X button to close the modal
 *   heading : can either be a string or a React.ReactNode. If the former, the headingStyle prop
 *             is used to style the text component wrapping the corresponding string. If the
 *             latter, headingStyle is ignored.
 *   description : similar to heading
 *   buttons : a list of buttons to follow the heading and the description
 */
export default function SimpleModal({
  isVisible,
  onClose,
  rodalStyle = {},
  showX = true,
  containerStyle = {},
  heading,
  headingStyle = {},
  description,
  descriptionStyle = {},
  warningMessage,
  buttons = [],
}: SimpleModalProps) {
  const [containerHeight, setContainerHeight] = useState(0);
  const [containerWidth, setContainerWidth] = useState(0);
  const defaultRodalStyle = Object.freeze(
    Object.assign(
      { borderRadius: 12 },
      containerHeight ? { height: containerHeight } : {},
      containerWidth ? { width: containerWidth } : {}
    )
  );
  const defaultContainerStyle = Object.freeze(
    Object.assign(
      {
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "space-between",
        flexGrow: 1,
        marginBottom: 20,
        marginHorizontal: 20,
      },
      showX || warningMessage ? {} : { marginTop: 20 }
    )
  );
  const defaultTextStyle = Object.freeze({
    fontSize: 18,
    color: skaleBlack,
    textAlign: "center",
  });
  const isString = (x) => {
    return typeof x === "string" || x instanceof String;
  };

  const xButton = (
    <View
      style={{
        alignItems: "flex-end",
        justifyContent: "flex-end",
        marginRight: 10,
        marginTop: 10,
      }}
    >
      <TouchableOpacity onPress={() => onClose()}>
        <Text
          style={{
            color: skaleBlack,
          }}
        >
          <Icon size={20} icon={closeRound} />
        </Text>
      </TouchableOpacity>
    </View>
  );

  return (
    <Rodal
      visible={isVisible}
      onClose={() => onClose()}
      customStyles={Object.assign({}, defaultRodalStyle, rodalStyle)}
    >
      <View
        onLayout={(event) => {
          setContainerHeight(event.nativeEvent.layout.height);
          setContainerWidth(event.nativeEvent.layout.width);
        }}
      >
        {showX && xButton}
        {warningMessage ? (
          <WarningBanner
            message={warningMessage}
            style={{ marginBottom: 11 }}
          />
        ) : null}
        <View style={Object.assign({}, defaultContainerStyle, containerStyle)}>
          {heading &&
            (isString(heading) ? (
              <Text
                isHeading={true}
                style={Object.assign({}, defaultTextStyle, headingStyle)}
              >
                {heading}
              </Text>
            ) : (
              heading
            ))}
          {description ? (
            isString(description) ? (
              <Text
                isHeading={false}
                style={Object.assign({}, defaultTextStyle, descriptionStyle)}
              >
                {description}
              </Text>
            ) : (
              description
            )
          ) : null}
          {buttons}
        </View>
      </View>
    </Rodal>
  );
}
